import React from "react";
import PropTypes from "prop-types";
import {
  SAMPLE_CLINICAL_FORM_DATA,
  CLINICAL_FORM_FIELD_LABELS,
} from "../../clinicalFormCatalog";
import { displayDateUS } from "../../../../utils/dateUtils";

/**
 * Fallback print sheet for form types that have no dedicated layout yet.
 * Renders whatever is in `formData` as a static label/value sheet, so every
 * row in the forms list can be printed the day it is created.
 */

const SHEET = "mx-auto w-full max-w-4xl";

const humanizeKey = (key) =>
  key.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, (c) => c.toUpperCase());

const fieldLabel = (key) => CLINICAL_FORM_FIELD_LABELS[key] || humanizeKey(key);

const isDateField = (key) => key.toLowerCase().endsWith("date");
const isWideField = (key) => !isDateField(key) && key !== "clinicianSignature";

const Row = ({ label, value, wide }) => (
  <div className={wide ? "sm:col-span-2" : ""}>
    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
      {label}
    </div>
    <div className="mt-0.5 min-h-[18px] whitespace-pre-wrap border-b border-slate-200 pb-0.5 text-[12px] text-slate-900">
      {value || "—"}
    </div>
  </div>
);

Row.propTypes = {
  label: PropTypes.string,
  value: PropTypes.node,
  wide: PropTypes.bool,
};

const GenericFormPrintSheet = ({ patient, clinicalForm }) => {
  const values = {
    ...SAMPLE_CLINICAL_FORM_DATA,
    ...(clinicalForm.formData || {}),
  };
  const patientName =
    [patient?.firstName, patient?.middleName, patient?.lastName]
      .filter(Boolean)
      .join(" ") || patient?.patientName;

  return (
    <div
      className={`${SHEET} rounded-xl border border-slate-200 bg-white px-5 py-6 shadow-sm sm:px-8`}
    >
      <header className="pb-3 text-center">
        <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
          {clinicalForm.formName}
        </h1>
      </header>

      <div className="grid grid-cols-2 gap-3 border-y border-slate-200 py-3 sm:grid-cols-4">
        <Row label="Patient Name" value={patientName} />
        <Row label="MRN" value={patient?.mrn} />
        <Row
          label="Date of Birth"
          value={displayDateUS(patient?.dateOfBirth)}
        />
        <Row label="Gender" value={patient?.gender} />
      </div>

      <div className="grid grid-cols-1 gap-x-6 gap-y-3 pt-4 sm:grid-cols-2">
        {Object.keys(values).map((key) => {
          const raw = values[key];
          return (
            <Row
              key={key}
              label={fieldLabel(key)}
              value={isDateField(key) ? displayDateUS(raw) : raw}
              wide={isWideField(key)}
            />
          );
        })}
      </div>

      {clinicalForm.reviewNotes && (
        <div className="mt-4 border-t border-slate-200 pt-3">
          <Row label="Reviewer Notes" value={clinicalForm.reviewNotes} wide />
        </div>
      )}

      <div className="mt-6 grid grid-cols-2 gap-6 text-[11px] text-slate-600">
        <div>
          <div className="h-8 border-b border-slate-400" />
          <div className="mt-1">
            Filled By {clinicalForm.filledByName || "—"}
            {clinicalForm.filledByRole
              ? ` (${clinicalForm.filledByRole})`
              : ""}
          </div>
        </div>
        <div>
          <div className="h-8 border-b border-slate-400" />
          <div className="mt-1">
            Reviewed By {clinicalForm.reviewedByName || "—"}
          </div>
        </div>
      </div>
    </div>
  );
};

GenericFormPrintSheet.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
};

export default GenericFormPrintSheet;
